/**
 * Read-only client for the backend version policy (phase C.3).
 *
 * Architectural rule for all of Phase C: the version middleware is the single
 * source of truth for compatibility. It validates the `X-App/Api/Sync-Version`
 * headers (sent in C.2) on every request and answers 426/423/503 when a client
 * is unsupported or maintenance is active — that enforcement path is handled by
 * the interceptor in C.5.
 *
 * This endpoint (`GET /api/v1/app/version/`) is *informational only*: it is
 * public, excluded from middleware enforcement, and returns the full policy as
 * a flat (non-enveloped, camelCase) payload. The frontend reads it to surface
 * "update available" / maintenance information to the user (C.4) — it never
 * compares versions to make a blocking decision. That is the middleware's job.
 */

import { httpClient, VERSION_CHECK_PATH } from '@/api/httpClient';

/** Maintenance posture advertised by the backend. Strict enum — do not extend. */
export type MaintenanceMode = 'none' | 'read_only' | 'full';

/** Flat, camelCase policy returned by `GET /api/v1/app/version/`. */
export type AppVersionPolicy = {
  latestVersion: string;
  latestBuildNumber: number;
  minimumSupportedVersion: string;
  minimumSupportedBuildNumber: number;
  minimumSyncSchemaVersion: number;
  currentSyncSchemaVersion: number;
  apiVersion: string;
  forceUpdate: boolean;
  maintenanceMode: MaintenanceMode;
  /** `null` (never `""`) when no deprecation warning is configured. */
  deprecationWarning: string | null;
  swForceUpdateEnabled: boolean;
  /** `null` when the backend is serving fail-open defaults (DB outage). */
  updatedAt: string | null;
};

/** Last successful policy plus when the client fetched it. */
export type CachedAppVersionPolicy = {
  policy: AppVersionPolicy;
  /** ISO timestamp of the successful fetch (client clock), distinct from `policy.updatedAt`. */
  cachedAt: string;
};

// Versioned key: bump the suffix whenever `AppVersionPolicy`'s shape changes so
// stale-shaped caches from older builds are orphaned automatically instead of
// being read and rejected by the guard. Mirrors the backend cache key
// (`platform_config:app_version_policy:v1`).
const CACHE_KEY = 'fuelops.app_version_policy.v1';

const MAINTENANCE_MODES: readonly MaintenanceMode[] = ['none', 'read_only', 'full'];

/**
 * Structural guard so a corrupt or stale-shaped cache entry never propagates as
 * a valid policy. Checks the fields downstream consumers rely on; the backend
 * owns the authoritative shape.
 */
const isAppVersionPolicy = (value: unknown): value is AppVersionPolicy => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const policy = value as Record<string, unknown>;

  return (
    typeof policy.latestVersion === 'string' &&
    typeof policy.latestBuildNumber === 'number' &&
    typeof policy.minimumSupportedVersion === 'string' &&
    typeof policy.minimumSupportedBuildNumber === 'number' &&
    typeof policy.minimumSyncSchemaVersion === 'number' &&
    typeof policy.currentSyncSchemaVersion === 'number' &&
    typeof policy.apiVersion === 'string' &&
    typeof policy.forceUpdate === 'boolean' &&
    MAINTENANCE_MODES.includes(policy.maintenanceMode as MaintenanceMode) &&
    (policy.deprecationWarning === null || typeof policy.deprecationWarning === 'string') &&
    typeof policy.swForceUpdateEnabled === 'boolean' &&
    (policy.updatedAt === null || typeof policy.updatedAt === 'string')
  );
};

/** Reads the last cached policy, or `null` when absent/corrupt. Never throws. */
export const readCachedAppVersionPolicy = (): CachedAppVersionPolicy | null => {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as unknown;

    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof (parsed as Record<string, unknown>).cachedAt === 'string' &&
      isAppVersionPolicy((parsed as Record<string, unknown>).policy)
    ) {
      return parsed as CachedAppVersionPolicy;
    }

    return null;
  } catch {
    return null;
  }
};

/** Persists a freshly fetched policy. Storage failures are swallowed (non-fatal). */
const writeCachedAppVersionPolicy = (policy: AppVersionPolicy): CachedAppVersionPolicy => {
  const entry: CachedAppVersionPolicy = {
    policy,
    cachedAt: new Date().toISOString()
  };

  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    // Quota/unavailable storage must not break the fetch path.
  }

  return entry;
};

export const appVersionApi = {
  /**
   * Fetches the current version policy. On success the response is cached and
   * returned; on any failure (offline, timeout, malformed body) it degrades to
   * the last cached policy, or `null` when nothing has ever been cached. A
   * failed check is never fatal — callers treat `null` as "unknown".
   */
  async fetchPolicy(): Promise<AppVersionPolicy | null> {
    try {
      const policy = await httpClient<AppVersionPolicy>(VERSION_CHECK_PATH, {
        skipAuth: true,
        envelope: 'raw'
      });

      if (!isAppVersionPolicy(policy)) {
        return readCachedAppVersionPolicy()?.policy ?? null;
      }

      writeCachedAppVersionPolicy(policy);
      return policy;
    } catch {
      return readCachedAppVersionPolicy()?.policy ?? null;
    }
  },
  readCached: readCachedAppVersionPolicy
};
